/**
 * This custom hook provides the functionality to retrieve users to be shown in the UI.
 */

import React, {useContext, useEffect, useState} from "react";
import {FirebaseAuthContext} from "../../../../commons/firebase/FirebaseAuthProvider";
import {useSnackbar} from "notistack";
import {useIntl} from "react-intl";
import CourseCard from "../../courses/card/course/CourseCard";
import {useConfirm} from "material-ui-confirm";

const useListUsers = () => {

    // State storage of the users shown in the table
    const [users, setUsers] = useState([])
    const [loading, setLoading] = useState(true)

    const [deletionInProgress, setDeletionInProgress] = useState(false)
    const [deletionAttemptedUser, setDeletionAttemptedUser] = useState(null)

    const {signedInUser} = useContext(FirebaseAuthContext);
    const confirm = useConfirm();

    const {enqueueSnackbar} = useSnackbar();

    // Internationalization for simple strings
    const intl = useIntl();

    // Retrieving the Moodle users and setting the list in the state variable
    const fetchUsers = async () => {
        const idToken = await signedInUser.getIdToken()
        return fetch('/reg-main/users?idToken=' + idToken)
            .then(async (data) => {
                // Throw an error including the textual status of the HTTP response code (no detailed message necessary)
                if (data.status !== 200) {
                    throw new Error((await data.json()).status)
                }

                const usersJson = await data.json();
                setUsers(usersJson)
            })
            .catch((error) => {
                enqueueSnackbar(
                    intl.formatMessage({
                        id: 'app.users.messages.list.error',
                        defaultMessage: 'Something went wrong while listing the users: {error}'
                    }, {error: error.message}), {
                        variant: 'error',
                        preventDuplicate: true,
                        autoHideDuration: 5000
                    });
            })
            .finally(() => {
                setLoading(false)
            })
    }

    useEffect(() => {
        fetchUsers();
    }, [])

    const handleDeleteUser = (user) => {
        setDeletionAttemptedUser(user.uid)
        return checkDeletionAllowed(user)
            .then((checkResult) => {
                setDeletionAttemptedUser(null)
                if (checkResult.allowed) {
                    if (!checkResult.courses) {
                        return confirm({
                            title: intl.formatMessage({
                                id: 'app.users.dialog.delete.confirm.title',
                                defaultMessage: 'Delete User'
                            }),
                            description: intl.formatMessage({
                                id: 'app.users.dialog.delete.confirm.description',
                                defaultMessage: 'Delete user {firstName} {secondName}?'
                            }, {firstName: user.firstName, secondName: user.secondName})
                        })
                            .then(() => 'OK')
                            .catch(() => null)
                    } else {
                        return confirm({
                            title: intl.formatMessage({
                                id: 'app.users.dialog.delete.existingReferences.title',
                                defaultMessage: 'Deletion has consequences'
                            }),
                            content: <div>
                                <p>{intl.formatMessage({
                                    id: 'app.users.dialog.delete.existingReferences.description',
                                    defaultMessage: '{firstName} {secondName} included in courses. Delete anyway?'
                                }, {firstName: user.firstName, secondName: user.secondName})}</p>
                                <div>
                                    {checkResult.courses.map((course) => <CourseCard key={course.key}
                                                                                     course={course}
                                                                                     textSize={12}
                                                                                     headerSize={14}/>)}
                                </div>
                            </div>
                        })
                            .then(() => 'OK')
                            .catch(() => null)
                    }
                } else {
                    return confirm({
                        title: intl.formatMessage({
                            id: 'app.users.dialog.delete.generalRejection.title',
                            defaultMessage: 'Deletion not allowed'
                        }),
                        content: <div>
                            {intl.formatMessage({
                                id: 'app.users.dialog.delete.generalRejection.description',
                                defaultMessage: 'The user is not allowed to be deleted - please contact your administrator.'
                            })}:
                        </div>
                    }).then(() => null).catch(() => null)
                }
            })
            .then((clickedButton) => {
                if (clickedButton === 'OK') {
                    return deleteUser(user)
                }
                return null
            })
            .catch((error) => {
                enqueueSnackbar(
                    intl.formatMessage({
                        id: 'app.users.messages.delete.error',
                        defaultMessage: 'Something went wrong while deleting the user: {error}'
                    }, {error: error.message}), {
                        variant: 'error',
                        preventDuplicate: true,
                        autoHideDuration: 5000
                    });
            })
            .finally(() => {
                setDeletionAttemptedUser(null)
                setDeletionInProgress(false)
            })
    }

    const checkDeletionAllowed = async (user) => {
        const idToken = await signedInUser.getIdToken()
        return fetch('/reg-main/users/' + user.uid + '/deletionAllowed?idToken=' + idToken)
            .then(async (data) => {
                // Throw an error including the textual status of the HTTP response code (no detailed message necessary)
                if (data.status !== 200) {
                    throw new Error((await data.json()).status)
                }
                return data.json();
            })
    }

    const deleteUser = async (user) => {
        setDeletionInProgress(true)
        const idToken = await signedInUser.getIdToken()
        return fetch('/reg-main/users/' + user.uid, {
            method: 'DELETE',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({
                idToken: idToken
            })
        }).then(async (data) => {
            // Throw an error including the textual status of the HTTP response code (no detailed message necessary)
            if (data.status !== 200) {
                throw new Error((await data.json()).status)
            }

            await fetchUsers()
            enqueueSnackbar(
                intl.formatMessage({
                    id: 'app.users.messages.delete.success',
                    defaultMessage: 'User {firstName} {secondName} deleted.'
                }, {firstName: user.firstName, secondName: user.secondName}), {
                    variant: 'success',
                    preventDuplicate: true,
                    autoHideDuration: 3000
                });
        })
    }

    const disableUser = async (user) => {
        const idToken = await signedInUser.getIdToken()
        return fetch('/reg-main/users/' + user.uid + '/disable', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({
                idToken: idToken
            })
        }).then(async (data) => {
            // Throw an error including the textual status of the HTTP response code (no detailed message necessary)
            if (data.status !== 200) {
                throw new Error((await data.json()).status)
            }

            await fetchUsers()
            enqueueSnackbar(
                intl.formatMessage({
                    id: 'app.users.messages.disable.success',
                    defaultMessage: 'User {firstName} {secondName} disabled.'
                }, {firstName: user.firstName, secondName: user.secondName}), {
                    variant: 'success',
                    preventDuplicate: true,
                    autoHideDuration: 3000
                });
        }).catch((error) => {
            enqueueSnackbar(
                intl.formatMessage({
                    id: 'app.users.messages.disable.error',
                    defaultMessage: 'Something went wrong while disabling the user: {error}'
                }, {error: error.message}), {
                    variant: 'error',
                    preventDuplicate: true,
                    autoHideDuration: 5000
                });
        })
    }

    const enableUser = async (user) => {
        const idToken = await signedInUser.getIdToken()
        return fetch('/reg-main/users/' + user.uid + '/enable', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({
                idToken: idToken
            })
        }).then(async (data) => {
            // Throw an error including the textual status of the HTTP response code (no detailed message necessary)
            if (data.status !== 200) {
                throw new Error((await data.json()).status)
            }

            await fetchUsers()
            enqueueSnackbar(
                intl.formatMessage({
                    id: 'app.users.messages.enable.success',
                    defaultMessage: 'User {firstName} {secondName} enabled.'
                }, {firstName: user.firstName, secondName: user.secondName}), {
                    variant: 'success',
                    preventDuplicate: true,
                    autoHideDuration: 3000
                });
        }).catch((error) => {
            enqueueSnackbar(
                intl.formatMessage({
                    id: 'app.users.messages.enable.error',
                    defaultMessage: 'Something went wrong while enabling the user: {error}'
                }, {error: error.message}), {
                    variant: 'error',
                    preventDuplicate: true,
                    autoHideDuration: 5000
                });
        })
    }

    return {users, loading, handleDeleteUser, deletionInProgress, deletionAttemptedUser, disableUser, enableUser}
}
export default useListUsers;